import {
  memo,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import clsx from 'clsx';
import type { StyleOption } from '../../../shared/api/contracts/styleTransferSchemas';
import { useTranslation } from 'react-i18next';

type StyleSelectorOption = StyleOption & {
  uiBatchId?: string;
};

export interface StyleSelectorProps {
  styles: StyleSelectorOption[];
  selectedStyleId: string | null;
  onSelect: (style: StyleOption) => void;
  isLoading?: boolean;
  disabled?: boolean;
  backgroundSelections?: Record<string, string | null | undefined>;
  onBackgroundSelect?: (styleId: string, colorCode: string) => void;
  infiniteScroll?: StyleSelectorInfiniteScrollOptions;
}

interface StyleSelectorInfiniteScrollOptions {
  hasMore: boolean;
  autoAdvanceEnabled: boolean;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
}

const hoverVariants = {
  rest: { scale: 1, borderColor: 'rgba(99,102,241,0.25)', boxShadow: '0 0 0 rgba(0,0,0,0)' },
  hover: {
    scale: 1.02,
    borderColor: 'rgba(99,102,241,0.6)',
    boxShadow: '0 12px 40px rgba(99,102,241,0.25)'
  }
};

const groupBackgroundVariants = [
  'border-ai-surface-glow/40 bg-ai-surface-elevated/80',
  'border-ai-primary/25 bg-ai-surface/40',
  'border-ai-secondary/25 bg-ai-surface-glow/30'
];

const DEFAULT_PREVIEW_BACKGROUNDS = [''];
const DEFAULT_PREVIEW_BACKGROUND_OPTIONS = DEFAULT_PREVIEW_BACKGROUNDS.map((value) => ({
  code: value,
  value,
  description: undefined
}));

const AUTO_ADVANCE_DELAY_MS = 300;
const AUTO_ADVANCE_THRESHOLD = 0.6;

interface PreviewBackgroundOption {
  code: string;
  value: string;
  description?: string | null;
}

export const StyleSelector = memo(function StyleSelector({
  styles,
  selectedStyleId,
  onSelect,
  isLoading,
  disabled = false,
  backgroundSelections,
  onBackgroundSelect,
  infiniteScroll
}: StyleSelectorProps) {
  const { t } = useTranslation();
  const numberFormatter = useMemo(
    () =>
      new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 1
      }),
    []
  );
  const infiniteScrollConfig = infiniteScroll ?? null;
  const {
    hasMore = false,
    autoAdvanceEnabled = false,
    isLoadingMore = false,
    onLoadMore
  } = infiniteScrollConfig ?? {};
  const showLoadMoreZone = Boolean(infiniteScrollConfig && hasMore);
  const loadMoreLabels = infiniteScrollConfig
    ? {
      hint: t('styleSelector.loadMoreHint'),
      loading: t('styleSelector.loadingMore'),
      button: t('styleSelector.loadMoreButton')
    }
    : null;
  const [previewIndex, setPreviewIndex] = useState<number | null>(null);
  const previewStyle = previewIndex !== null ? styles[previewIndex] ?? null : null;
  const dialogContainerRef = useRef<HTMLDivElement | null>(null);
  const previewImageRef = useRef<HTMLImageElement | null>(null);
  const portalContainerRef = useRef<HTMLDivElement | null>(null);
  const previewBackgroundSelectionsRef = useRef(new Map<string, string>());
  const autoAdvanceSentinelRef = useRef<HTMLDivElement | null>(null);
  const autoAdvanceLockRef = useRef(false);
  const autoAdvanceTimeoutRef = useRef<number | null>(null);
  const scrollLockRef = useRef<{
    scrollX: number;
    scrollY: number;
    style: {
      overflow: string;
      paddingRight: string;
    };
  } | null>(null);
  const [, setPreviewBadgeInset] = useState<{ bottom: number; right: number }>({
    bottom: 24,
    right: 24
  });
  const [previewBackground, setPreviewBackground] = useState<string>(
    DEFAULT_PREVIEW_BACKGROUND_OPTIONS[0]?.value ?? '#050816'
  );

  const invokeAutoAdvance = useCallback(() => {
    if (!autoAdvanceEnabled || !onLoadMore || autoAdvanceLockRef.current) {
      return;
    }
    autoAdvanceLockRef.current = true;
    onLoadMore();
  }, [autoAdvanceEnabled, onLoadMore]);

  const closePreview = useCallback(() => {
    setPreviewIndex(null);
  }, []);

  const goToPreviousPreview = useCallback(() => {
    setPreviewIndex((index) => {
      if (index === null || index <= 0) {
        return index;
      }
      return index - 1;
    });
  }, []);

  const goToNextPreview = useCallback(() => {
    setPreviewIndex((index) => {
      if (index === null || index >= styles.length - 1) {
        return index;
      }
      return index + 1;
    });
  }, [styles.length]);

  useEffect(() => {
    if (!previewStyle) {
      return;
    }
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        closePreview();
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goToPreviousPreview();
        return;
      }
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        goToNextPreview();
      }
    };
    window.addEventListener('keydown', handleKey, { passive: false });
    return () => {
      window.removeEventListener('keydown', handleKey);
    };
  }, [previewStyle, closePreview, goToPreviousPreview, goToNextPreview]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    if (previewStyle && !scrollLockRef.current) {
      const body = document.body;
      const docElement = document.documentElement;
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;

      scrollLockRef.current = {
        scrollX,
        scrollY,
        style: {
          overflow: body.style.overflow,
          paddingRight: body.style.paddingRight
        }
      };

      const scrollbarWidth = window.innerWidth - docElement.clientWidth;
      body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        body.style.paddingRight = `${scrollbarWidth}px`;
      }
      return;
    }

    if (!previewStyle && scrollLockRef.current) {
      const { scrollX, scrollY, style } = scrollLockRef.current;
      const body = document.body;

      body.style.overflow = style.overflow;
      body.style.paddingRight = style.paddingRight;

      scrollLockRef.current = null;
      window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
    }
  }, [previewStyle]);

  useEffect(() => {
    return () => {
      if (typeof window === 'undefined') {
        return;
      }
      if (scrollLockRef.current) {
        const { scrollX, scrollY, style } = scrollLockRef.current;
        const body = document.body;

        body.style.overflow = style.overflow;
        body.style.paddingRight = style.paddingRight;

        scrollLockRef.current = null;
        window.scrollTo({ left: scrollX, top: scrollY, behavior: 'auto' });
      }
    };
  }, []);

  useEffect(() => {
    if (!previewStyle) {
      return;
    }
    dialogContainerRef.current?.focus({ preventScroll: true });
  }, [previewStyle]);

  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }
    const container = document.createElement('div');
    container.setAttribute('data-style-selector-portal', 'true');
    document.body.appendChild(container);
    portalContainerRef.current = container;
    return () => {
      document.body.removeChild(container);
      portalContainerRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!showLoadMoreZone || !autoAdvanceEnabled || !onLoadMore) {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }
    const target = autoAdvanceSentinelRef.current;
    if (!target) {
      return;
    }

    const clearPending = () => {
      if (autoAdvanceTimeoutRef.current !== null) {
        window.clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }
        if (!entry.isIntersecting) {
          clearPending();
          if (!isLoadingMore) {
            autoAdvanceLockRef.current = false;
          }
          return;
        }
        if (autoAdvanceLockRef.current || autoAdvanceTimeoutRef.current !== null) {
          return;
        }
        autoAdvanceTimeoutRef.current = window.setTimeout(() => {
          autoAdvanceTimeoutRef.current = null;
          invokeAutoAdvance();
        }, AUTO_ADVANCE_DELAY_MS);
      },
      { threshold: AUTO_ADVANCE_THRESHOLD }
    );

    observer.observe(target);
    return () => {
      observer.disconnect();
      clearPending();
    };
  }, [autoAdvanceEnabled, invokeAutoAdvance, isLoadingMore, onLoadMore, showLoadMoreZone]);

  useEffect(() => {
    if (!autoAdvanceEnabled) {
      autoAdvanceLockRef.current = false;
      if (autoAdvanceTimeoutRef.current !== null) {
        window.clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
      return;
    }
    if (!isLoadingMore) {
      autoAdvanceLockRef.current = false;
    }
  }, [autoAdvanceEnabled, isLoadingMore]);

  useEffect(() => {
    if (!hasMore) {
      autoAdvanceLockRef.current = false;
      if (autoAdvanceTimeoutRef.current !== null) {
        window.clearTimeout(autoAdvanceTimeoutRef.current);
        autoAdvanceTimeoutRef.current = null;
      }
    }
  }, [hasMore]);

  useEffect(() => {
    if (!previewStyle) {
      setPreviewBadgeInset({ bottom: 24, right: 24 });
    }
  }, [previewStyle]);

  useEffect(() => {
    if (!disabled) {
      return;
    }
    setPreviewIndex(null);
  }, [disabled]);

  const updatePreviewBadgeInset = useCallback(() => {
    const container = dialogContainerRef.current;
    const image = previewImageRef.current;
    if (!container || !image) {
      setPreviewBadgeInset({ bottom: 24, right: 24 });
      return;
    }

    const containerRect = container.getBoundingClientRect();
    const imageRect = image.getBoundingClientRect();

    const bottomGap = Math.max(containerRect.bottom - imageRect.bottom, 0);
    const rightGap = Math.max(containerRect.right - imageRect.right, 0);

    const nextBottom = bottomGap > 0 ? Math.max(bottomGap / 2, 16) : 24;
    const nextRight = rightGap > 0 ? Math.max(rightGap / 2, 16) : 24;

    setPreviewBadgeInset({ bottom: nextBottom, right: nextRight });
  }, []);

  useLayoutEffect(() => {
    if (!previewStyle) {
      return;
    }
    updatePreviewBadgeInset();
  }, [previewStyle, updatePreviewBadgeInset]);

  useEffect(() => {
    if (!previewStyle) {
      return;
    }
    const handleResize = () => {
      updatePreviewBadgeInset();
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [previewStyle, updatePreviewBadgeInset]);

  const handlePreviewToggle = useCallback(() => {
    if (disabled) {
      return;
    }
    if (!previewStyle) {
      return;
    }
    onSelect(previewStyle);
    requestAnimationFrame(() => {
      updatePreviewBadgeInset();
    });
  }, [disabled, onSelect, previewStyle, updatePreviewBadgeInset]);

  const handlePreviewSelect = useCallback(() => {
    if (disabled || !previewStyle) {
      return;
    }
    if (previewStyle.id !== selectedStyleId) {
      onSelect(previewStyle);
    }
    closePreview();
  }, [closePreview, disabled, onSelect, previewStyle, selectedStyleId]);

  const previewBackgroundOptions = useMemo<PreviewBackgroundOption[]>(() => {
    if (!previewStyle) {
      return DEFAULT_PREVIEW_BACKGROUND_OPTIONS;
    }
    if (previewStyle.backgroundColors?.length) {
      const seen = new Set<string>();
      return previewStyle.backgroundColors
        .map((option) => ({
          code: option.colorCode,
          value: option.colorValue,
          description: option.colorDescription ?? null
        }))
        .filter((option) => {
          if (seen.has(option.code)) {
            return false;
          }
          seen.add(option.code);
          return true;
        });
    }
    return DEFAULT_PREVIEW_BACKGROUND_OPTIONS;
  }, [previewStyle]);

  useEffect(() => {
    if (!previewStyle) {
      setPreviewBackground(DEFAULT_PREVIEW_BACKGROUND_OPTIONS[0]?.value ?? '#050816');
      return;
    }

    if (previewStyle.backgroundColors?.length) {
      const selectionCode = backgroundSelections?.[previewStyle.id] ?? null;
      const matchedOption =
        previewBackgroundOptions.find((option) => option.code === selectionCode) ??
        previewBackgroundOptions[0];
      if (matchedOption) {
        setPreviewBackground(matchedOption.value);
        return;
      }
    }

    const stored = previewBackgroundSelectionsRef.current.get(previewStyle.id);
    if (stored) {
      const storedOption = previewBackgroundOptions.find((option) => option.value === stored);
      if (storedOption) {
        setPreviewBackground(storedOption.value);
        return;
      }
    }

    setPreviewBackground(
      previewBackgroundOptions[0]?.value ?? DEFAULT_PREVIEW_BACKGROUND_OPTIONS[0]?.value ?? '#050816'
    );
  }, [backgroundSelections, previewBackgroundOptions, previewStyle]);

  const handleBackgroundSelect = useCallback(
    (option: PreviewBackgroundOption) => {
      if (!previewStyle || disabled) {
        return;
      }
      if (previewStyle.backgroundColors?.length) {
        onBackgroundSelect?.(previewStyle.id, option.code);
      } else {
        previewBackgroundSelectionsRef.current.set(previewStyle.id, option.value);
      }
      setPreviewBackground(option.value);
    },
    [disabled, onBackgroundSelect, previewStyle]
  );

  const defaultGroupLabel = t('styleSelector.defaultGroup');

  const groupedStyles = useMemo(() => {
    if (!styles.length) {
      return [];
    }

    const groups: Array<{
      key: string;
      title: string;
      items: Array<{ style: StyleSelectorOption; index: number }>;
    }> = [];
    const keyIndex = new Map<string, number>();

    styles.forEach((style, index) => {
      const batchKey = style.uiBatchId ?? '__batch-default__';
      const groupKey = style.groupCode ?? '__ungrouped__';
      const compositeKey = `${batchKey}::${groupKey}`;
      const title = style.groupName ?? style.groupCode ?? defaultGroupLabel;
      const existing = keyIndex.get(compositeKey);
      const entry = { style, index };

      if (existing === undefined) {
        keyIndex.set(compositeKey, groups.length);
        groups.push({
          key: compositeKey,
          title,
          items: [entry]
        });
      } else {
        groups[existing].items.push(entry);
      }
    });

    return groups;
  }, [styles, defaultGroupLabel]);

  if (isLoading) {
    return (
      <div className="grid w-full gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 6 }).map((_, index) => (
          <div
            key={index}
            className="animate-pulse rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/60 p-5"
          >
            <div className="mb-4 h-32 rounded-xl bg-ai-surface-glow/30" />
            <div className="h-4 rounded bg-ai-surface-glow/30" />
          </div>
        ))}
      </div>
    );
  }

  if (!groupedStyles.length) {
    return (
      <div className="rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 p-6 text-center text-sm text-ai-text-secondary">
        {t('styleSelector.empty')}
      </div>
    );
  }

  const hasPreviousPreview = previewIndex !== null && previewIndex > 0;
  const hasNextPreview = previewIndex !== null && previewIndex < styles.length - 1;

  return (
    <div className="flex flex-col gap-8" aria-disabled={disabled}>
      {groupedStyles.map((group, groupIndex) => {
        const backgroundClass =
          groupBackgroundVariants[groupIndex % groupBackgroundVariants.length];
        return (
          <section
            key={group.key}
            className={clsx(
              'overflow-hidden rounded-3xl border p-4 shadow-[0_12px_45px_rgba(14,21,45,0.35)]',
              backgroundClass,
              disabled ? 'opacity-60 transition-opacity' : ''
            )}
          >
            <div className={clsx(group.title ? 'mt-0' : '', 'grid gap-4 sm:grid-cols-2 lg:grid-cols-3')}>
              {group.items.map(({ style, index }) => {
                const isSelected = style.id === selectedStyleId;
                const costMultiplier = style.costMultiplier ?? 1;
                const costBadgeLabel = t('styleSelector.costBadge', {
                  credits: numberFormatter.format(costMultiplier)
                });
                const backgroundOptions = style.backgroundColors ?? [];
                const fallbackBackgroundCode =
                  style.defaultBackgroundColorCode ?? backgroundOptions[0]?.colorCode ?? null;
                const selectedBackgroundCode = backgroundOptions.length
                  ? backgroundSelections?.[style.id] ?? null
                  : null;
                const resolvedBackgroundCode =
                  selectedBackgroundCode &&
                    backgroundOptions.some((option) => option.colorCode === selectedBackgroundCode)
                    ? selectedBackgroundCode
                    : fallbackBackgroundCode;
                const resolvedBackgroundValue = resolvedBackgroundCode
                  ? backgroundOptions.find((option) => option.colorCode === resolvedBackgroundCode)?.colorValue ??
                  resolvedBackgroundCode
                  : null;
                const previewThumbnailStyle = resolvedBackgroundValue
                  ? { background: resolvedBackgroundValue }
                  : undefined;
                return (
                  <motion.div
                    key={style.id}
                    initial="rest"
                    whileHover={disabled ? undefined : 'hover'}
                    animate={!disabled && isSelected ? 'hover' : 'rest'}
                    variants={hoverVariants}
                    transition={{ type: 'spring', stiffness: 220, damping: 20 }}
                    className={clsx(
                      'group relative flex flex-col items-start overflow-hidden rounded-2xl border bg-ai-surface-elevated/60 p-5 text-left shadow-ai-card focus:outline-none',
                      'focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface',
                      isSelected ? 'border-ai-primary/70' : 'border-ai-surface-glow/25',
                      disabled ? 'cursor-not-allowed' : 'cursor-pointer'
                    )}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-pressed={isSelected}
                    aria-disabled={disabled}
                    onClick={() => {
                      if (disabled) {
                        return;
                      }
                      onSelect(style);
                    }}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        if (disabled) {
                          return;
                        }
                        onSelect(style);
                      }
                    }}
                  >
                    <div
                      className="relative h-36 w-full overflow-hidden rounded-xl bg-ai-surface-glow/20"
                      style={previewThumbnailStyle}
                    >
                      {style.previewUrl ? (
                        <button
                          type="button"
                          className="group/preview absolute inset-0"
                          disabled={disabled}
                          onClick={(event) => {
                            if (disabled) {
                              event.preventDefault();
                              return;
                            }
                            if (event.target instanceof HTMLImageElement) {
                              event.stopPropagation();
                              setPreviewIndex(index);
                            }
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.stopPropagation();
                              if (disabled) {
                                return;
                              }
                              setPreviewIndex(index);
                            }
                          }}
                          aria-label={t('styleSelector.viewOriginalLabel', { style: style.name })}
                        >
                          <img
                            src={style.previewUrl}
                            alt={t('styleSelector.previewAlt', { style: style.name })}
                            className="h-full w-full object-cover transition-transform duration-300 ease-out group-hover/preview:scale-105"
                            data-style-selector-image="true"
                            loading="lazy"
                          />
                        </button>
                      ) : (
                        <div className="flex h-full w-full items-center justify-center text-xs text-ai-text-secondary">
                          {t('styleSelector.noPreview')}
                        </div>
                      )}
                      {isSelected && (
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-ai-primary/30 via-transparent to-ai-secondary/20" />
                      )}
                    </div>
                    <div className="mt-4 flex w-full flex-wrap items-center justify-center gap-2 text-center">
                      <span className="font-medium text-ai-text-primary">{style.name}</span>
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide',
                          isSelected
                            ? 'bg-ai-primary/15 text-ai-primary'
                            : 'bg-ai-surface/80 text-ai-text-secondary/80'
                        )}
                        aria-label={costBadgeLabel}
                        title={costBadgeLabel}
                      >
                        <span aria-hidden="true" className="text-[0.75rem] leading-none">
                          ⚡
                        </span>
                        <span>{costBadgeLabel}</span>
                      </span>
                      <div
                        className={clsx(
                          'relative inline-flex items-center',
                          isSelected ? 'w-full justify-center' : 'gap-2'
                        )}
                      >
                        {/* Animated Arrow pointing to the button */}
                        <svg
                          viewBox="0 0 70 55"
                          fill="none"
                          className={clsx(
                            'pointer-events-none transition-opacity duration-300 ease-out',
                            !isSelected
                              ? 'h-16 w-20 opacity-0 -translate-x-3 scale-75 group-hover:opacity-100 group-hover:translate-x-0 group-hover:scale-100'
                              : 'h-0 w-0 opacity-0'
                          )}
                          aria-hidden="true"
                        >
                          {/* Curved arrow with gradient and glow */}
                          <defs>
                            <linearGradient id={`arrow-gradient-${style.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                              <stop offset="0%" stopColor="rgb(147, 51, 234)" stopOpacity="1" />
                              <stop offset="50%" stopColor="rgb(99, 102, 241)" stopOpacity="1" />
                              <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="1" />
                            </linearGradient>
                            <filter id={`arrow-glow-${style.id}`} x="-50%" y="-50%" width="200%" height="200%">
                              <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                              <feMerge>
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="coloredBlur" />
                                <feMergeNode in="SourceGraphic" />
                              </feMerge>
                            </filter>
                            <filter id={`arrow-pulse-${style.id}`}>
                              <feGaussianBlur stdDeviation="1" result="blur" />
                              <feMerge>
                                <feMergeNode in="blur" />
                                <feMergeNode in="SourceGraphic" />
                              </feMerge>
                            </filter>
                          </defs>

                          {/* Continuous arrow path: quarter circle + shaft */}
                          <path
                            d="M 30 40 A 18 18 0 0 1 48 22 L 50 22"
                            stroke={`url(#arrow-gradient-${style.id})`}
                            strokeWidth="6"
                            strokeLinecap="round"
                            fill="none"
                            filter={`url(#arrow-glow-${style.id})`}
                            className="group-hover:animate-pulse"
                          />

                          {/* Filled arrow head - positioned at shaft end */}
                          <path
                            d="M 64 22 L 48 14 L 50 22 L 48 30 Z"
                            fill={`url(#arrow-gradient-${style.id})`}
                            filter={`url(#arrow-pulse-${style.id})`}
                            className="group-hover:animate-pulse"
                          />
                        </svg>

                        {/* Selection status button with breathing effect */}
                        <span
                          className={clsx(
                            'pointer-events-none inline-flex items-center justify-center rounded-full border transition-[transform,opacity,background-color,border-color,box-shadow] duration-300',
                            isSelected
                              ? 'h-9 w-9 border-ai-accent/80 bg-ai-accent/20 text-ai-accent shadow-[0_12px_36px_rgba(255,193,7,0.45)] backdrop-blur-md ring-2 ring-ai-accent/50'
                              : [
                                'h-6 w-6 border-ai-surface-glow/40 bg-ai-surface/70 text-ai-text-secondary/70',
                                'group-hover:border-ai-primary/70 group-hover:bg-ai-primary/25 group-hover:text-ai-primary group-hover:shadow-[0_8px_24px_rgba(99,102,241,0.45)] group-hover:animate-pulse group-focus-visible:border-ai-primary/70 group-focus-visible:bg-ai-primary/25 group-focus-visible:text-ai-primary group-focus-visible:shadow-[0_8px_24px_rgba(99,102,241,0.45)]'
                              ]
                          )}
                          aria-hidden="true"
                        >
                          <svg
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth={3}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            className={clsx(
                              'transition-all duration-300',
                              isSelected ? 'h-5 w-5 opacity-100' : 'h-3.5 w-3.5 opacity-70'
                            )}
                          >
                            <polyline points="5 13 9 17 19 7" />
                          </svg>
                        </span>
                      </div>
                      {resolvedBackgroundValue && backgroundOptions.length > 0 && (
                        <button
                          type="button"
                          className={clsx(
                            'inline-flex h-7 items-center gap-1 rounded-full border border-ai-surface-glow/40 bg-ai-surface/80 px-2 py-0.5 text-[11px] font-medium text-ai-text-secondary/80 shadow-sm transition',
                            disabled
                              ? 'cursor-not-allowed opacity-70'
                              : 'hover:border-ai-primary/70 hover:text-ai-primary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface'
                          )}
                          onClick={(event) => {
                            event.stopPropagation();
                            if (disabled) {
                              return;
                            }
                            setPreviewIndex(index);
                          }}
                          onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              event.stopPropagation();
                              if (disabled) {
                                return;
                              }
                              setPreviewIndex(index);
                            }
                          }}
                          aria-label={t('styleSelector.viewOriginalLabel', { style: style.name })}
                        >
                          <span
                            className="h-4 w-4 rounded-full border border-white/30 shadow-inner"
                            style={{ background: resolvedBackgroundValue }}
                            aria-hidden="true"
                          />
                        </button>
                      )}
                    </div>
                    <span className="sr-only">
                      {isSelected ? t('styleSelector.selected') : t('styleSelector.selectPrompt')}
                    </span>
                  </motion.div>
                );
              })}
            </div>
          </section>
        );
      })}
      {showLoadMoreZone ? (
        <div className="flex flex-col items-center gap-3 text-xs text-ai-text-secondary">
          <div className="inline-flex items-center gap-2 rounded-full border border-ai-surface-glow/40 bg-ai-surface/70 px-4 py-2">
            {isLoadingMore ? (
              <span className="flex h-3 w-3 animate-spin rounded-full border-2 border-ai-primary border-b-transparent" />
            ) : (
              <span className="h-2 w-2 rounded-full bg-ai-primary" />
            )}
            <span>{isLoadingMore ? loadMoreLabels?.loading : loadMoreLabels?.hint}</span>
          </div>
          <button
            type="button"
            className={clsx(
              'inline-flex items-center justify-center rounded-full border border-ai-primary/30 px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus',
              disabled || isLoadingMore
                ? 'cursor-not-allowed opacity-50'
                : 'text-ai-primary hover:border-ai-primary hover:bg-ai-primary/15'
            )}
            onClick={() => {
              if (disabled || isLoadingMore) {
                return;
              }
              onLoadMore?.();
            }}
            disabled={disabled || isLoadingMore}
          >
            {loadMoreLabels?.button}
          </button>
          <div
            ref={autoAdvanceSentinelRef}
            className="h-6 w-full rounded-full bg-transparent"
            aria-hidden="true"
          />
        </div>
      ) : null}
      {previewStyle && portalContainerRef.current
        ? createPortal(
          <div
            className="fixed inset-0 z-50 overflow-y-auto bg-black/80"
            role="dialog"
            aria-modal="true"
            aria-label={t('styleSelector.previewDialogTitle', { style: previewStyle.name })}
            onClick={closePreview}
          >
            <div className="flex min-h-full items-start justify-center p-6">
              <div
                className="relative w-full max-w-2xl rounded-2xl border border-ai-surface-glow/30 bg-ai-surface shadow-2xl"
                ref={dialogContainerRef}
                tabIndex={-1}
                onClick={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  className="absolute right-6 top-6 inline-flex h-11 w-11 items-center justify-center rounded-full border border-ai-surface-glow/40 bg-ai-surface-elevated/80 text-ai-text-primary shadow-ai-card transition hover:scale-105 hover:border-ai-primary hover:bg-ai-primary/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface"
                  onClick={(event) => {
                    event.stopPropagation();
                    closePreview();
                  }}
                  aria-label={t('styleSelector.closePreview')}
                >
                  <svg
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className="h-5 w-5"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
                {hasPreviousPreview && (
                  <button
                    type="button"
                    className="absolute left-6 top-1/2 flex h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full bg-ai-surface-glow/80 text-ai-text-primary shadow-ai-card transition hover:scale-110 hover:bg-ai-primary/80 hover:shadow-ai-glow hover:animate-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface animate-pulse"
                    onClick={(event) => {
                      event.stopPropagation();
                      goToPreviousPreview();
                    }}
                    aria-label={t('styleSelector.previousPreview')}
                  >
                    <span className="sr-only">{t('styleSelector.previousPreview')}</span>
                    <span aria-hidden="true" className="text-2xl font-semibold">
                      ‹
                    </span>
                  </button>
                )}
                {hasNextPreview && (
                  <button
                    type="button"
                    className="absolute right-6 top-1/2 flex h-16 w-16 -translate-y-1/2 items-center justify-center rounded-full bg-ai-surface-glow/80 text-ai-text-primary shadow-ai-card transition hover:scale-110 hover:bg-ai-primary/80 hover:shadow-ai-glow hover:animate-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface animate-pulse"
                    onClick={(event) => {
                      event.stopPropagation();
                      goToNextPreview();
                    }}
                    aria-label={t('styleSelector.nextPreview')}
                  >
                    <span className="sr-only">{t('styleSelector.nextPreview')}</span>
                    <span aria-hidden="true" className="text-2xl font-semibold">
                      ›
                    </span>
                  </button>
                )}
                <div className="group flex flex-col gap-0 p-3">
                  <div className="inline-flex w-full justify-center">
                    <img
                      src={previewStyle.previewUrl ?? undefined}
                      alt={t('styleSelector.previewAlt', { style: previewStyle.name })}
                      style={{ background: previewBackground }}
                      className="max-h-[70vh] w-full max-w-full cursor-pointer rounded-3xl object-contain object-top"
                      ref={previewImageRef}
                      onLoad={updatePreviewBadgeInset}
                      role="button"
                      tabIndex={0}
                      aria-pressed={previewStyle.id === selectedStyleId}
                      onClick={(event) => {
                        event.stopPropagation();
                        handlePreviewToggle();
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          event.stopPropagation();
                          handlePreviewToggle();
                        }
                      }}
                    />
                  </div>
                  <div
                    className={clsx(
                      'flex items-center gap-0 rounded-2xl border border-ai-surface-glow/30 bg-ai-surface-elevated/70 p-2',
                      disabled ? 'cursor-not-allowed opacity-70' : 'cursor-pointer'
                    )}
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    aria-pressed={previewStyle.id === selectedStyleId}
                    onClick={(event) => {
                      if (disabled) {
                        return;
                      }
                      event.stopPropagation();
                      handlePreviewToggle();
                    }}
                    onKeyDown={(event) => {
                      if (disabled) {
                        return;
                      }
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        event.stopPropagation();
                        handlePreviewToggle();
                      }
                    }}
                  >
                    <div className="flex flex-wrap items-center justify-center gap-3 flex-1">
                      {previewBackgroundOptions.map((option) => {
                        const isActive = option.value === previewBackground;
                        const descriptionLabel = option.description?.trim();
                        const fallbackDescription = t('styleSelector.color_description');
                        const displayLabel = descriptionLabel
                          ? `${descriptionLabel} (${option.value})`
                          : `${fallbackDescription} (${option.value})`;
                        return (
                          <button
                            key={option.code}
                            type="button"
                            className={clsx(
                              'relative flex h-12 w-12 items-center justify-center rounded-lg border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface',
                              isActive
                                ? 'border-ai-primary ring-1 ring-ai-primary/80'
                                : 'border-ai-surface-glow/50 hover:border-ai-primary/70'
                            )}
                            style={{ background: option.value }}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleBackgroundSelect(option);
                            }}
                            aria-pressed={isActive}
                            aria-label={displayLabel}
                            title={displayLabel}
                          >
                            {isActive ? (
                              <svg
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth={3}
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="h-4 w-4 text-[#1f2937]"
                              >
                                <polyline points="5 13 9 17 19 7" />
                              </svg>
                            ) : null}
                          </button>
                        );
                      })}
                    </div>
                    <div className="relative inline-flex items-center">
                      {/* Animated Arrow pointing to the select button in preview modal */}
                      <svg
                        viewBox="0 0 70 55"
                        fill="none"
                        className={clsx(
                          'pointer-events-none transition-opacity duration-300 ease-out',
                          previewStyle.id !== selectedStyleId
                            ? 'h-16 w-16 opacity-0 -translate-x-3 scale-75 group-hover:opacity-100 group-hover:translate-x-0 group-hover:scale-100'
                            : 'h-0 w-0 opacity-0'
                        )}
                        aria-hidden="true"
                      >
                        <defs>
                          <linearGradient id={`preview-arrow-gradient-${previewStyle.id}`} x1="0%" y1="0%" x2="100%" y2="0%">
                            <stop offset="0%" stopColor="rgb(147, 51, 234)" stopOpacity="1" />
                            <stop offset="50%" stopColor="rgb(99, 102, 241)" stopOpacity="1" />
                            <stop offset="100%" stopColor="rgb(59, 130, 246)" stopOpacity="1" />
                          </linearGradient>
                          <filter id={`preview-arrow-glow-${previewStyle.id}`} x="-50%" y="-50%" width="200%" height="200%">
                            <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
                            <feMerge>
                              <feMergeNode in="coloredBlur" />
                              <feMergeNode in="coloredBlur" />
                              <feMergeNode in="SourceGraphic" />
                            </feMerge>
                          </filter>
                        </defs>
                        <path
                          d="M 30 40 A 18 18 0 0 1 48 22 L 50 22"
                          stroke={`url(#preview-arrow-gradient-${previewStyle.id})`}
                          strokeWidth="6"
                          strokeLinecap="round"
                          fill="none"
                          filter={`url(#preview-arrow-glow-${previewStyle.id})`}
                          className="group-hover:animate-pulse"
                        />
                        <path
                          d="M 64 22 L 48 14 L 50 22 L 48 30 Z"
                          fill={`url(#preview-arrow-gradient-${previewStyle.id})`}
                          className="group-hover:animate-pulse"
                        />
                      </svg>

                      <button
                        type="button"
                        className={clsx(
                          'flex h-10 w-10 items-center justify-center rounded-full border transition-all duration-300',
                          disabled
                            ? 'cursor-not-allowed opacity-60'
                            : 'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai-focus focus-visible:ring-offset-2 focus-visible:ring-offset-ai-surface',
                          previewStyle.id === selectedStyleId
                            ? 'border-ai-accent/80 bg-ai-accent/20 text-ai-accent shadow-[0_12px_36px_rgba(255,193,7,0.45)] backdrop-blur-md ring-2 ring-ai-accent/50'
                            : [
                              'border-ai-surface-glow/40 bg-ai-surface/70 text-ai-text-secondary/70',
                              'group-hover:border-ai-primary/70 group-hover:bg-ai-primary/25 group-hover:text-ai-primary group-hover:shadow-[0_8px_24px_rgba(99,102,241,0.45)] group-hover:animate-pulse'
                            ]
                        )}
                        disabled={disabled}
                        onClick={(event) => {
                          event.stopPropagation();
                          handlePreviewSelect();
                        }}
                        aria-label={t('styleSelector.selectPrompt')}
                      >
                        <svg
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={3}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className={clsx(
                            'h-4 w-4 transition-opacity duration-200',
                            previewStyle.id === selectedStyleId ? 'opacity-100' : 'opacity-70'
                          )}
                        >
                          <polyline points="5 13 9 17 19 7" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          portalContainerRef.current
        )
        : null}
    </div>
  );
});
